import { useQuery } from "@apollo/client";
import React, { ReactElement, useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import StyledLoading from "../../components/atoms/Loading/StyledLoading";
import Button from "../../components/atoms/Button";
import FilterWinesToggleButton from "../../components/atoms/FilterWinesToggleButton";
import Tabs from "../../components/atoms/Tabs";
import Tooltip from "../../components/atoms/Tooltip";
import PageMeta from "../../components/molecules/PageMeta";
import MerchCardController from "../../components/molecules/MerchCardController";
import RewardCollectionGallery from "../../components/molecules/RewardCollectionGallery";
import RewardsFilterTray from "../../components/organisms/RewardsFilterTray";
import RewardMerchDetailsTray, {
  RewardMerchTrayContent,
} from "../../components/organisms/RewardMerchDetailsTray";
import { GET_MEMBER_REWARD_BASIC_INFO } from "../../graphql/resolvers/member";
import {
  GET_ALL_REWARD_ITEMS,
  GET_REWARD_COLLECTIONS,
  GET_REWARD_COLLECTION,
} from "../../graphql/resolvers/reward";
import { isLoggedIn } from "../../helpers/auth";
import { FETCH_POLICY_CACHE_AND_NETWORK, I18N_NAMESPACES } from "../../helpers/constants";
import { logError } from "../../helpers/logging";
import { getRewardsTabs } from "../../helpers/navbarProcessor";
import { MemberRewardBasicInfo } from "../../types/__generated__/MemberRewardBasicInfo";
import { RewardStore, RewardStoreVariables } from "../../types/__generated__/RewardStore";
import { RewardStoreItemFragment as RewardNode } from "../../types/__generated__/RewardStoreItemFragment";
import { RewardCollections } from "../../types/__generated__/RewardCollections";
import { RewardCollection, RewardCollectionVariables } from "../../types/__generated__/RewardCollection";
import { RewardStoreItemOrderField } from "../../types/__generated__/globalTypes";
import urlPatterns from "../../urls";
import { useLayoutContext } from "../../components/organisms/Layout/context";
import { colors } from "../../styles/variables";
import { useDeviceSize } from "../../helpers/hooks";
import { checkIfRequestIsFromMobileApp } from "../../helpers/tools";
import {
  PageWrapper,
  PageTitleWrapper,
  PageTitleContainer,
  PageTitle,
  StoreContainer,
  ColComponent,
  StoreListWrapper,
  StoreToolbar,
  StoreOptions,
  StoreOption,
  StoreSelectors,
  SortControl,
  SortSelect,
  FilterControl,
  ResultsHeader,
  EmptyState,
  CollectionHeading,
  CollectionTitle,
} from "./style";
import {
  getRewardsStoreOptions,
  getRewardsStoreSearch,
  getRewardsFilterCount,
  matchesRewardPointRanges,
  REWARD_SORT_FIELDS,
  RewardsStoreFilters,
} from "./helpers";

const RewardsStore = () => {
  const { t } = useTranslation([
    I18N_NAMESPACES.COMMON,
    I18N_NAMESPACES.REWARDS,
    I18N_NAMESPACES.TOP_NAVBAR,
    I18N_NAMESPACES.WINE_PAGES,
  ]);
  const { setShouldOpenBoxAndCartTray } = useLayoutContext();
  const location = useLocation<{ shouldOpenBoxAndCartTray?: boolean }>();
  const history = useHistory();
  const { slug } = useParams<{ slug?: string }>();
  // The store lands on collections: both the bare store URL and the collections URL show them.
  const pathname = location.pathname.replace(/\/$/, "");
  const isCollections = [urlPatterns.REWARDS_STORE, urlPatterns.REWARD_COLLECTIONS].some(
    (url) => url.replace(/\/$/, "") === pathname
  );
  const isAllRewards = !isCollections && !slug;
  const { orderBy, filters } = getRewardsStoreOptions(location.search);
  const filterCount = getRewardsFilterCount(filters);
  const isUserLoggedIn = isLoggedIn();
  const { isSmall } = useDeviceSize();
  const isTabBarHidden = isSmall && checkIfRequestIsFromMobileApp();

  const [selectedRewardNode, setSelectedRewardNode] = useState<RewardNode | undefined | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [rewardMerchTrayInitialContent, setRewardMerchTrayInitialContent] = useState<RewardMerchTrayContent>(
    RewardMerchTrayContent.RewardMerchDetails
  );

  // We only support point redemption in the reward store
  const redeemWithPoints = true;

  const { data: memberInfo, error: memberInfoQueryError } = useQuery<MemberRewardBasicInfo>(
    GET_MEMBER_REWARD_BASIC_INFO,
    {
      fetchPolicy: FETCH_POLICY_CACHE_AND_NETWORK,
      skip: !isUserLoggedIn,
    }
  );

  const {
    data: allRewardsData,
    error: allRewardsQueryError,
    loading: isAllRewardsDataLoading,
    refetch: refetchAllRewards,
  } = useQuery<RewardStore, RewardStoreVariables>(GET_ALL_REWARD_ITEMS, {
    partialRefetch: true,
    skip: !isAllRewards,
    variables: {
      isRedeemableWithPoints: true,
      orderBy: orderBy ? REWARD_SORT_FIELDS[orderBy] : undefined,
      minPoints: filters.minPoints,
      maxPoints: filters.maxPoints,
      colourIds: filters.colourIds.length ? filters.colourIds : undefined,
    },
  });

  const collectionsQuery = useQuery<RewardCollections>(GET_REWARD_COLLECTIONS, { skip: !isCollections });
  const collectionQuery = useQuery<RewardCollection, RewardCollectionVariables>(GET_REWARD_COLLECTION, {
    skip: !slug,
    variables: { slug: slug || "", orderBy },
  });
  const collection = slug ? collectionQuery.data?.rewardCollectionBySlug : undefined;
  const activeCollectionQuery = slug ? collectionQuery : collectionsQuery;
  const rewardsError = isAllRewards ? allRewardsQueryError : activeCollectionQuery.error;
  const isLoading = isAllRewards ? isAllRewardsDataLoading : activeCollectionQuery.loading;

  const changeSorting = (sort?: RewardStoreItemOrderField) => {
    history.push({
      pathname: slug ? urlPatterns.REWARD_COLLECTION(slug) : urlPatterns.ALL_REWARDS,
      search: getRewardsStoreSearch(isAllRewards ? filters : { colourIds: [] }, sort),
    });
  };

  const applyFilters = (nextFilters: RewardsStoreFilters) => {
    setIsFiltersOpen(false);
    history.push({
      pathname: urlPatterns.ALL_REWARDS,
      search: getRewardsStoreSearch(nextFilters, orderBy),
    });
  };

  useEffect(() => {
    // 'shouldOpenBoxAndCartTray' is set to true when navigating from "Build your own box"
    if (location.state?.shouldOpenBoxAndCartTray) {
      setShouldOpenBoxAndCartTray(location.state.shouldOpenBoxAndCartTray);
    }
  }, [location.state?.shouldOpenBoxAndCartTray]);

  if (memberInfoQueryError)
    logError(memberInfoQueryError, { location: "RewardsStore", action: "GET_MEMBER_REWARD_BASIC_INFO" });
  useEffect(() => {
    if (rewardsError) logError(rewardsError, { location: "RewardsStore", action: "LOAD_REWARDS" });
  }, [rewardsError]);

  const handleClick = (
    event: React.MouseEvent<HTMLElement>,
    startingContent: RewardMerchTrayContent,
    rewardNode: RewardNode | undefined | null
  ) => {
    if (!event?.metaKey) {
      event?.preventDefault();
      setSelectedRewardNode(rewardNode);
      setRewardMerchTrayInitialContent(startingContent);
    }
  };

  const renderRewardDetailsSideTray = (rewardStoreProductId: number): ReactElement => (
    <RewardMerchDetailsTray
      rewardStoreProductId={rewardStoreProductId}
      onRewardMerchDetailsClose={() => setSelectedRewardNode(null)}
      memberId={memberInfo?.me?.id}
      memberLevel={memberInfo?.me?.level?.rank}
      pointsBalance={memberInfo?.me?.pointsBalance}
      isOnMonthlyBoxToggle={memberInfo?.me?.shoppingCart?.shouldWinesBeAddedToSubscription}
      initialContent={rewardMerchTrayInitialContent}
    />
  );

  const renderPageTitleSection = (): ReactElement => (
    <PageTitleWrapper>
      <PageTitleContainer>
        <PageTitle>{t("topNavbar:rewardStore")}</PageTitle>
      </PageTitleContainer>
    </PageTitleWrapper>
  );

  const renderMerchCard = (rewardNode: RewardNode | null | undefined): ReactElement | null =>
    rewardNode && rewardNode.product.id ? (
      <ColComponent xl={3} lg={4} md={4} sm={6} xs={6} key={rewardNode?.product?.id}>
        <MerchCardController
          productSlug={rewardNode.product.slug}
          memberLevel={memberInfo?.me?.level?.rank}
          name={rewardNode.product.name}
          image={rewardNode.product.coverPhotoSmallPng}
          isNew={!!rewardNode.isNew}
          isLowStock={!!rewardNode.isLowStock}
          hasStock={!!rewardNode.hasStock}
          isCollected={!!rewardNode?.isCollected}
          unlockLevel={rewardNode.product.unlockedAtLevel?.rank}
          redeemWithPoints={redeemWithPoints}
          memberPointsBalance={memberInfo?.me?.pointsBalance}
          pointsCost={rewardNode.product.pointCost}
          cashCost={rewardNode.product.sellingPrice}
          onClick={(event: React.MouseEvent<HTMLElement>, initialContent: RewardMerchTrayContent) =>
            handleClick(event, initialContent, rewardNode)
          }
          characteristics={rewardNode.product.characteristics}
          coverPhotoBackgroundColor={colors.merchCardDefaultBackgroundColor}
        />
      </ColComponent>
    ) : null;

  const rewards = isAllRewards
    ? allRewardsData?.allRewardStoreItems?.edges.map((edge) => edge?.node)
    : collection?.items?.filter((item) => item?.product.isRedeemableWithPoints);
  // The API accepts one points interval; omit gaps between separately selected bands here.
  const visibleRewards =
    rewards?.filter(
      (reward) =>
        reward &&
        reward.product.pointCost !== null &&
        (!isAllRewards || matchesRewardPointRanges(reward.product.pointCost, filters.pointRanges))
    ) || [];
  const collections =
    collectionsQuery.data?.rewardCollections?.filter(
      (rewardCollection) =>
        rewardCollection?.slug &&
        rewardCollection.items?.some(
          (item) => item?.product.isRedeemableWithPoints && item.product.pointCost !== null
        )
    ) || [];

  const renderStoreList = (): ReactElement => (
    <>
      {collection && (
        <CollectionHeading>
          <CollectionTitle>{collection.name}</CollectionTitle>
          {collection.description && <Tooltip tootipTitle={collection.description} />}
        </CollectionHeading>
      )}
      <ResultsHeader>
        <span role="status">{t("rewards:store.resultCount", { count: visibleRewards.length })}</span>
        {isAllRewards && !!filterCount && (
          <Button ghost onClick={() => applyFilters({ colourIds: [] })}>
            {t("rewards:store.clearFilters")}
          </Button>
        )}
      </ResultsHeader>
      {visibleRewards.length ? (
        <StoreListWrapper>{visibleRewards.map(renderMerchCard)}</StoreListWrapper>
      ) : (
        <EmptyState>
          {slug && !collection ? t("rewards:store.collectionNotFound") : t("rewards:store.noResults")}
        </EmptyState>
      )}
    </>
  );

  const renderContent = (): ReactElement => {
    if (isLoading) return <StyledLoading />;
    if (rewardsError) {
      return (
        <EmptyState role="alert">
          <p>{t("rewards:store.loadError")}</p>
          <Button
            onClick={() => {
              if (isAllRewards) refetchAllRewards();
              else if (slug) collectionQuery.refetch();
              else collectionsQuery.refetch();
            }}
          >
            {t("rewards:store.retry")}
          </Button>
        </EmptyState>
      );
    }
    if (!isCollections) return renderStoreList();
    if (!collections.length) return <EmptyState>{t("rewards:store.noCollections")}</EmptyState>;
    return (
      <>
        {collections.map(
          (rewardCollection) =>
            rewardCollection && (
              <RewardCollectionGallery
                key={rewardCollection.id}
                collection={rewardCollection}
                renderReward={renderMerchCard}
              />
            )
        )}
      </>
    );
  };

  const translatedTabs = getRewardsTabs(isUserLoggedIn).map((tab) => ({
    url: tab.url,
    text: t(tab.i18nKey),
    exact: tab.url !== urlPatterns.REWARDS_STORE,
  }));

  return (
    <PageWrapper>
      <PageMeta
        title={t("rewards:pageMeta.store.title")}
        description={t("rewards:pageMeta.store.description")}
      />
      {renderPageTitleSection()}
      {!isTabBarHidden && <Tabs tabs={translatedTabs} />}
      <StoreContainer>
        <StoreToolbar>
          <StoreOptions aria-label={t("rewards:store.views")}>
            <StoreOption
              to={urlPatterns.ALL_REWARDS}
              selected={isAllRewards}
              aria-current={isAllRewards ? "page" : undefined}
            >
              {t("rewards:store.allRewards")}
            </StoreOption>
            <StoreOption
              to={urlPatterns.REWARD_COLLECTIONS}
              selected={!isAllRewards}
              aria-current={!isAllRewards ? "page" : undefined}
            >
              {t("common:collections")}
            </StoreOption>
          </StoreOptions>
          <StoreSelectors>
            <SortControl>
              <span>{t("winePages:sort")}</span>
              <SortSelect
                aria-label={t("winePages:sort")}
                value={isCollections ? "" : orderBy || ""}
                onChange={(event) =>
                  changeSorting((event.target.value || undefined) as RewardStoreItemOrderField | undefined)
                }
              >
                <option value="" />
                {Object.values(RewardStoreItemOrderField).map((sort) => (
                  <option key={sort} value={sort}>
                    {t(`rewards:store.sort.${sort}`)}
                  </option>
                ))}
              </SortSelect>
            </SortControl>
            <FilterControl>
              <FilterWinesToggleButton
                toggleFilterWines={() => setIsFiltersOpen(true)}
                numberOfSelectedFilters={isAllRewards ? filterCount : 0}
              />
            </FilterControl>
          </StoreSelectors>
        </StoreToolbar>
        {renderContent()}
      </StoreContainer>
      {isFiltersOpen && (
        <RewardsFilterTray
          filters={isAllRewards ? filters : { colourIds: [] }}
          onApply={applyFilters}
          onClose={() => setIsFiltersOpen(false)}
        />
      )}
      {selectedRewardNode?.product?.id && renderRewardDetailsSideTray(selectedRewardNode?.product?.id)}
    </PageWrapper>
  );
};

export default RewardsStore;
